import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@akuna/ui";
import { useStayPlanStatus } from "@/hooks/useStayPlanStatus";
import { filterStayPlanAlertsForComanda, planChargeIsInComanda, type StayPlanAlertKind, type StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { formatDateBR } from "@/lib/date-utils";
import {
  dismissStayPlanAlert,
  clearExpiredStayPlanAlertDismissals,
  isStayPlanAlertDismissed,
  stayPlanAlertDismissalKey,
  type DismissibleStayPlanAlert,
} from "./stayPlanAlertDismissalState";

const ALERT_COPY: Record<StayPlanAlertKind, { key: string; variant: "danger" | "warning" }> = {
  overdue: { key: "planAlerts.overdue", variant: "danger" },
  due_soon: { key: "planAlerts.dueSoon", variant: "warning" },
  last_cycle_unit: { key: "planAlerts.lastCycleUnit", variant: "warning" },
  last_plan_unit: { key: "planAlerts.lastPlanUnit", variant: "warning" },
  last_week_unit: { key: "planAlerts.lastWeekUnit", variant: "warning" },
  week_quota_exceeded: { key: "planAlerts.weekQuotaExceeded", variant: "warning" },
};

/** Parte pura: recebe os alertas prontos, para poder ser testada sem rede. */
export function StayPlanAlertList({
  alerts,
  nextDueOn,
  planName,
  petId,
  contractId,
  day,
}: {
  alerts: readonly StayPlanAlertKind[];
  nextDueOn: string | null;
  planName: string;
  petId: string;
  contractId: string;
  day: string;
}) {
  const { t } = useTranslation("daycareHotel");
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<StayPlanAlertKind>>(() => new Set());

  useEffect(() => {
    setDismissedAlerts(new Set());
    if (typeof window !== "undefined") {
      try {
        clearExpiredStayPlanAlertDismissals(window.localStorage);
      } catch {
        // Em navegadores que bloqueiam o armazenamento, o estado local continua funcional.
      }
    }
  }, [day, petId, contractId]);

  if (alerts.length === 0) return null;

  const isDismissed = (alert: DismissibleStayPlanAlert) => {
    if (dismissedAlerts.has(alert)) return true;
    if (typeof window === "undefined") return false;

    try {
      return isStayPlanAlertDismissed(
        window.localStorage,
        stayPlanAlertDismissalKey({ day, petId, contractId, alert }),
      );
    } catch {
      return false;
    }
  };

  const dismiss = (alert: DismissibleStayPlanAlert) => {
    const key = stayPlanAlertDismissalKey({ day, petId, contractId, alert });
    try {
      if (typeof window !== "undefined") dismissStayPlanAlert(window.localStorage, key);
    } catch {
      // Em navegadores que bloqueiam o armazenamento, o estado local ainda fecha o alerta nesta tela.
    }
    setDismissedAlerts((current) => new Set(current).add(alert));
  };

  return (
    <div className="grid gap-2">
      {alerts.map((alert) => {
        if (isDismissed(alert)) return null;

        return (
          <Alert key={alert} variant={ALERT_COPY[alert].variant} closeLabel={t("planAlerts.dismiss")} onClose={() => dismiss(alert)}>
            {t(ALERT_COPY[alert].key, { planName, dueOn: nextDueOn ? formatDateBR(nextDueOn) : "" })}
          </Alert>
        );
      })}
    </div>
  );
}

/**
 * Os quatro alertas de plano (vencido, vence em 7 dias, última diária do
 * ciclo, último agendamento do pacote), usados na reserva, no check-in, no
 * acompanhamento e no checkout.
 */
export function StayPlanAlerts({
  module,
  petId,
  date,
  comandaLines = [],
}: {
  module: StayPlanModule;
  petId: string | null;
  date: string;
  /** Linhas da comanda aberta da estadia, para não repetir o aviso de mensalidade já lançada ali. */
  comandaLines?: Array<{ itemName: string }>;
}) {
  const { t } = useTranslation("daycareHotel");
  const { status } = useStayPlanStatus({ module, petId, date });
  if (!status) return null;

  const isChargeInComanda = planChargeIsInComanda({
    planName: status.contract.planName,
    membershipLineLabel: t("planContract.membershipLine"),
    lines: comandaLines,
  });

  return (
    <StayPlanAlertList
      alerts={filterStayPlanAlertsForComanda(status.alerts, isChargeInComanda)}
      nextDueOn={status.contract.nextDueOn}
      planName={status.contract.planName}
      petId={status.contract.petId}
      contractId={status.contract.id}
      day={date}
    />
  );
}
